import { useState, useEffect } from "react";
import { logInfo, logWarn } from "@/lib/monitoring";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Lock, LogIn, Eye, EyeOff, ArrowLeft } from "lucide-react";
import ThemeLogo from "@/components/ThemeLogo";
import { registerSession } from "@/hooks/useSessionGuard";
import { SessionConflictDialog } from "@/components/SessionConflictDialog";
import { SEO } from "@/components/SEO";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { capitalizeName } from "@/lib/utils";
import {
  checkLoginRateLimit,
  recordFailedLogin,
  resetLoginAttempts,
  validatePassword,
  sanitizeEmail,
  sanitizeText,
} from "@/lib/security";

const Login = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { session, loading, signIn, redirectByRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Preserva ?next= (usado pelo fluxo de consentimento OAuth /.lovable/oauth/consent)
  const nextParam = new URLSearchParams(window.location.search).get("next");
  const safeNext = nextParam && /^\/(?!\/)/.test(nextParam) ? nextParam : null;

  useEffect(() => {
    if (!loading && session?.user) {
      if (safeNext) {
        window.location.href = safeNext;
        return;
      }
      redirectByRole(session.user.id);
    }
  }, [session, loading, safeNext]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    // Frontend rate limit (first layer)
    const rateCheck = checkLoginRateLimit();
    if (!rateCheck.allowed) {
      toast({
        variant: "destructive",
        title: t("login.rateLimitTitle"),
        description: t("login.rateLimitDescription", { seconds: rateCheck.remainingSeconds }),
      });
      return;
    }
    const cleanEmail = sanitizeEmail(email);
    if (!cleanEmail || !password.trim()) {
      toast({ variant: "destructive", title: t("login.requiredFields"), description: t("login.fillEmailPassword") });
      return;
    }
    setIsLoading(true);
    try {
      // Server-side rate limit (per-email, can't be bypassed by clearing localStorage / incognito)
      const { data: emailRl } = await supabase.rpc("check_login_rate_limit_by_email", { p_email: cleanEmail });
      const emailRlResult = emailRl as any;
      if (emailRlResult && emailRlResult.allowed === false) {
        toast({
          variant: "destructive",
          title: t("login.rateLimitTitle"),
          description: t("login.rateLimitDescription", { seconds: emailRlResult.retry_after_seconds || 60 }),
        });
        setIsLoading(false);
        return;
      }

      // Database-level rate limit check (per-IP layer — kept for legacy)
      const { data: rlData } = await supabase.rpc("check_login_rate_limit", { p_ip: "web-client" });
      const rlResult = rlData as any;
      if (rlResult && !rlResult.allowed) {
        toast({
          variant: "destructive",
          title: t("login.rateLimitTitle"),
          description: t("login.rateLimitDescription", { seconds: rlResult.retry_after_seconds || 60 }),
        });
        setIsLoading(false);
        return;
      }


      const userId = await signIn(cleanEmail, password);
      resetLoginAttempts();
      logInfo("Login bem-sucedido", { userId, email: cleanEmail.replace(/(.{2}).*(@.*)/, "$1***$2") });

      // Record successful login in DB
      await supabase.rpc("record_login_attempt", { p_ip: "web-client", p_email: cleanEmail, p_success: true });

      // Check for existing session on another device
      if (userId) {
        const { hadExisting, registered } = await registerSession(userId);
        if (hadExisting && !registered) {
          setPendingUserId(userId);
          setSessionConflict(true);
          setIsLoading(false);
          return;
        }
      }
    } catch (error: any) {
      recordFailedLogin();
      logWarn("Falha no login", { email: cleanEmail.replace(/(.{2}).*(@.*)/, "$1***$2"), reason: error.message });
      // Record failed login in DB
      try { await supabase.rpc("record_login_attempt", { p_ip: "web-client", p_email: cleanEmail, p_success: false }); } catch {}
      toast({
        variant: "destructive",
        title: t("login.loginError"),
        description: error.message === "Invalid login credentials"
          ? t("login.invalidCredentials")
          : error.message || t("login.invalidCredentials"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceSession = async () => {
    if (!pendingUserId) return;
    setIsLoading(true);
    try {
      await registerSession(pendingUserId, true);
      setSessionConflict(false);
      setPendingUserId(null);
      await redirectByRole(pendingUserId);
    } catch {
      toast({ variant: "destructive", title: "Erro", description: "Não foi possível iniciar a sessão." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSession = async () => {
    setSessionConflict(false);
    setPendingUserId(null);
    await supabase.auth.signOut();
  };

  // Signup removed — all accounts are provisioned by Dev/SuperAdmin/Admin

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <>
    <SEO
      title="Entrar — Submission-BJJ"
      description="Acesse sua conta na plataforma Submission-BJJ para gerenciar sua academia, turmas, presenças e financeiro."
      path="/login"
      noindex
    />
    <SessionConflictDialog
      open={sessionConflict}
      onConfirm={handleForceSession}
      onCancel={handleCancelSession}
      loading={isLoading}
    />
    <main className="flex min-h-screen">
      {/* Left side – decorative */}
      <div className="hidden lg:flex lg:w-1/2 gradient-primary items-center justify-center relative overflow-hidden pearl-weave-texture">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-primary-foreground/20 blur-3xl" />
          <div className="absolute bottom-32 right-16 w-96 h-96 rounded-full bg-primary-foreground/10 blur-3xl" />
        </div>
        <div className="relative z-10 text-center px-12 animate-fade-in">
          <img src="/logo-dark.png" alt="Submission BJJ Red Line" className="w-40 h-40 mx-auto mb-6 object-contain" fetchPriority="high" width={160} height={160} />
          <h2 className="text-4xl font-display font-bold text-primary-foreground mb-4">
            {t("login.platformTitle")}
          </h2>
          <p className="text-primary-foreground/80 text-lg max-w-md">
            {t("login.platformDescription")}
          </p>
        </div>
      </div>

      {/* Right side – form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-8 relative">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <div className="flex items-center gap-1 bg-card/80 backdrop-blur-sm border border-border rounded-full px-2 py-1">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => i18n.changeLanguage(lang.code)}
                title={lang.label}
                aria-label={`Mudar idioma para ${lang.label}`}
                aria-pressed={i18n.language === lang.code}
                className={cn(
                  "text-base sm:text-lg leading-none rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center transition-all",
                  i18n.language === lang.code
                    ? "bg-primary/15 ring-2 ring-primary scale-110"
                    : "hover:bg-muted opacity-70 hover:opacity-100"
                )}
              >
                <span aria-hidden="true">{lang.flag}</span>
              </button>
            ))}
          </div>
          <ThemeToggle />
        </div>
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-3">
            <ThemeLogo className="h-40 w-auto" />
          </div>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-display font-bold text-foreground">
              {t("login.welcome")}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t("login.subtitle")}
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground font-medium">{t("login.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" placeholder={t("login.emailPlaceholder")} value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12 bg-card border-border focus-visible:ring-primary" autoComplete="email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground font-medium">{t("login.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" type={showPassword ? "text" : "password"} placeholder={t("login.passwordPlaceholder")} value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10 h-12 bg-card border-border focus-visible:ring-primary" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <a href="/esqueci-senha" className="text-sm text-primary hover:text-primary/80 font-medium transition-colors">{t("login.forgotPassword")}</a>
            </div>
            <Button type="submit" disabled={isLoading} className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-base hover:opacity-90 transition-opacity">
              {isLoading ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />{t("login.signingIn")}</span>
              ) : (
                <span className="flex items-center gap-2"><LogIn className="w-4 h-4" />{t("login.signIn")}</span>
              )}
            </Button>
            <div className="text-center">
              <a href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" />
                {t("login.backToHome", "Voltar à página inicial")}
              </a>
            </div>
          </form>
        </div>
      </div>
    </main>
    </>
  );
};

export default Login;
